import { FormlyFieldConfig } from '@ngx-formly/core';
import { RktFormFieldConfig, formInput, formInputAddress, formRepeatSection, formRow } from '@rocketfinancialcorp/rocket-ui/form';

import { Patterns } from '@shared/components';
import { ADDRESS_COUNTRY_RESTRICTIONS, POSTAL_CODE_LABEL, STATE_PROVINCE_LABEL } from '@shared/constants/identity-formats';

export const addressFormFields = (isPhysicalAddressRequired: boolean): RktFormFieldConfig[] => {
  return [
    ...getAddressFields({
      type: 'Physical',
      key: 'physicalAddresses',
      isRequired: isPhysicalAddressRequired,
    }),
    ...getAddressFields({
      type: 'Mailing',
      key: 'mailingAddresses',
      isRequired: false,
    }),
    ...getAddressFields({
      type: 'Shipping',
      key: 'shippingAddresses',
      isRequired: false,
    }),
  ];
};

export const getAddressFields = ({
  type,
  key,
  isRequired,
}: {
  type: 'Physical' | 'Mailing' | 'Shipping';
  key: string;
  isRequired?: boolean;
}): RktFormFieldConfig[] => {
  const expressions = {
    'props.required': (field: FormlyFieldConfig) => {
      if (isRequired) {
        return true;
      }

      return (
        !!field.model?.addressLine1 ||
        !!field.model?.addressLine2 ||
        !!field.model?.city ||
        !!field.model?.state ||
        !!field.model?.postalCode ||
        !!field.model?.country
      );
    },
  };

  return [
    formRepeatSection({
      key: `${key}`,
      unstyled: true,
      addText: `Add ${type} Address`,
      removeText: 'Remove',
      header: `${type} Address`,
      subheader: type === 'Physical' ? 'Must not be a PO box' : undefined,
      notAllowEmpty: true,
      fieldGroup: [
        formRow(
          [
            formInput({
              key: 'firstName',
              label: 'First Name',
              props: {
                pattern: Patterns.GENERIC_NAME,
                minLength: 1,
                maxLength: 40,
              },
              validation: { messages: { pattern: 'Enter a valid first name' } },
            }),
            formInput({
              key: 'middleName',
              label: 'Middle Name',
              props: {
                pattern: Patterns.GENERIC_NAME,
                minLength: 1,
                maxLength: 40,
              },
              validation: { messages: { pattern: 'Enter a valid middle name' } },
            }),
            formInput({
              key: 'lastName',
              label: 'Last Name',
              props: {
                pattern: Patterns.GENERIC_NAME,
                minLength: 1,
                maxLength: 40,
              },
              validation: { messages: { pattern: 'Enter a valid last name' } },
            }),
          ],
          { isHidden: type !== 'Shipping' },
        ),
        formRow([
          formInputAddress({
            key: 'addressLine1',
            label: 'Address 1',
            props: {
              countryRestrictions: ADDRESS_COUNTRY_RESTRICTIONS,
              minLength: 1,
              maxLength: 50,
            },
            expressions,
          }),
        ]),
        formRow([
          formInput({
            key: 'addressLine2',
            label: `Address 2`,
            props: { minLength: 1, maxLength: 50 },
          }),
        ]),
        formRow([
          formInput({ key: 'city', label: 'City', className: 'city-field', props: { minLength: 1, maxLength: 25 }, expressions }),
          formInput({
            key: 'state',
            label: STATE_PROVINCE_LABEL,
            className: 'state-field',
            props: { minLength: 2, maxLength: 3 },
            validators: { validation: [{ name: 'isValidStateCode' }] },
            expressions,
          }),
          formInput({
            key: 'postalCode',
            label: POSTAL_CODE_LABEL,
            className: 'postal-code-field',
            props: { minLength: 1, maxLength: 10 },
            expressions,
          }),
        ]),
        formRow([
          formInput({
            key: 'country',
            label: 'Country',
            parsers: [(value?: string) => value?.toUpperCase()],
            props: { minLength: 3, maxLength: 3 },
            validators: { validation: [{ name: 'isValidCountryCode' }] },
            validation: {
              messages: {
                minLength: 'Must be 3 characters',
              },
            },
            expressions,
          }),
        ]),
      ],
    }),
  ];
};
